import React, { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';

// 별 모양 SVG path (RealTransactionHybridCard에서 사용하는 것과 동일)
const STAR_SYMBOL_PATH = 'path://M512 64l128.6 260.6L928 366.4l-208 202.7L769.2 864 512 729.6 254.8 864 304 569.1 96 366.4l287.4-41.8L512 64z';

const toDateKey = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  const normalized = text.replace(/[./]/g, '-');
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;
  if (/^\d{4}-\d{2}$/.test(normalized)) return `${normalized}-01`;
  if (/^\d{6}$/.test(text)) {
    const yy = Number(text.slice(0, 2));
    const yyyy = yy >= 70 ? 1900 + yy : 2000 + yy;
    return `${yyyy}-${text.slice(2, 4)}-${text.slice(4, 6)}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  if (/^\d{8}$/.test(text)) return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`;
  if (/^\d{4}-\d{2}-\d{2}/.test(normalized)) return normalized.slice(0, 10);
  return normalized.length >= 10 ? normalized.slice(0, 10) : normalized;
};

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// 날짜 표기 통일: YY.MM.DD (일 없으면 YY.MM)
const formatDotDate = (value) => {
  const d = toDateKey(value);
  const m = String(d || '').match(/^(\d{4})-(\d{2})(?:-(\d{2}))?/);
  if (!m) return d ? d.replace(/-/g, '.') : '-';
  const yy = m[1].slice(2);
  return m[3] ? `${yy}.${m[2]}.${m[3]}` : `${yy}.${m[2]}`;
};

// 억 소수1자리 표기 (원 단위 입력) — 예: 24.3억, 24억
const formatEok1 = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return '-';
  const r = Math.round((num / 100000000) * 10) / 10;
  return `${Number.isInteger(r) ? r : r.toFixed(1)}억`;
};

function AskPriceChartSection({ marketItems = [], targetPrice = null, viewMode: parentViewMode = 'sale', onViewModeChange, loading = false }) {
  const [mode, setMode] = useState('sale');
  const [viewMode, setViewMode] = useState('monthly'); // 'monthly' | 'weekly'
  const chartRef = React.useRef(null);

  React.useEffect(() => {
    if (parentViewMode) {
      setMode(parentViewMode);
    }
  }, [parentViewMode]);

  const handleModeChange = (newMode) => {
    setMode(newMode);
    if (onViewModeChange) {
      onViewModeChange(newMode);
    }
  };

  React.useEffect(() => {
    console.log('AskPriceChartSection - targetPrice:', targetPrice, 'mode:', mode);
  }, [targetPrice, mode]);

  const { labels, upperSeries, avgSeries, lowerSeries, rangeText, minLowerPoint, tableData } = useMemo(() => {
    // marketItems에서 date, salePrice/leasePrice, saleCount/leaseCount 추출
    const items = (Array.isArray(marketItems) ? marketItems : [])
      .map((row) => ({
        date: toDateKey(row?.date),
        salePrice: toNum(row?.salePrice),
        leasePrice: toNum(row?.leasePrice),
        saleMaxPrice: toNum(row?.saleMaxPrice),
        saleMinPrice: toNum(row?.saleMinPrice),
        leaseMaxPrice: toNum(row?.leaseMaxPrice),
        leaseMinPrice: toNum(row?.leaseMinPrice),
        saleCount: toNum(row?.saleCount),
        leaseCount: toNum(row?.leaseCount),
        dataSource: row?.dataSource,
      }))
      .filter((x) => x.date);

    // 배열 평균(빈 배열이면 null) — 표의 평균호가 계산용
    const meanOf = (arr) => (Array.isArray(arr) && arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : null);

    if (viewMode === 'weekly') {
      // 주별 표시
      const sortedItems = [...items].sort((a, b) => a.date.localeCompare(b.date));
      const dates = sortedItems.map(item => item.date);
      const upper = sortedItems.map(item => mode === 'sale' ? item.salePrice : item.leasePrice);
      const lower = upper; // 주별에는 상위/하위 구분 없음
      const avg = upper;

      const tableRows = sortedItems.map(item => ({
        date: item.date,
        salePrice: item.salePrice,
        leasePrice: item.leasePrice,
        upperSale: item.salePrice,
        lowerSale: item.salePrice,
        avgSale: item.salePrice,
        upperLease: item.leasePrice,
        lowerLease: item.leasePrice,
        avgLease: item.leasePrice,
      }));

      const allPrices = upper.filter((v) => Number.isFinite(v));
      const minVal = allPrices.length ? Math.min(...allPrices) : null;
      const maxVal = allPrices.length ? Math.max(...allPrices) : null;
      const range = minVal != null && maxVal != null ? `${formatEok1(minVal * 10000)} ~ ${formatEok1(maxVal * 10000)}` : '-';

      return {
        labels: dates,
        upperSeries: upper,
        avgSeries: avg,
        lowerSeries: lower,
        rangeText: range,
        minLowerPoint: null,
        tableData: tableRows,
      };
    }

    // 월별로 그룹핑
    const byMonth = new Map();
    items.forEach((item) => {
      const month = item.date.slice(0, 7); // YYYY-MM
      if (!byMonth.has(month)) {
        byMonth.set(month, {
          salePrices: [],
          leasePrices: [],
          saleCounts: [],
          leaseCounts: [],
          // 실거래 데이터용 max/min (dataSource === 'real_transaction'인 경우)
          saleMaxPrice: null,
          saleMinPrice: null,
          leaseMaxPrice: null,
          leaseMinPrice: null,
          dataSource: null,
        });
      }
      const monthData = byMonth.get(month);

      // dataSource가 real_transaction이면 max/min을 직접 사용
      if (item.dataSource === 'real_transaction') {
        monthData.dataSource = 'real_transaction';
        if (item.saleMaxPrice !== null) monthData.saleMaxPrice = item.saleMaxPrice;
        if (item.saleMinPrice !== null) monthData.saleMinPrice = item.saleMinPrice;
        if (item.leaseMaxPrice !== null) monthData.leaseMaxPrice = item.leaseMaxPrice;
        if (item.leaseMinPrice !== null) monthData.leaseMinPrice = item.leaseMinPrice;
        // 실거래의 평균값도 저장
        if (item.salePrice !== null) monthData.salePrices.push(item.salePrice);
        if (item.leasePrice !== null) monthData.leasePrices.push(item.leasePrice);
      } else {
        // weekly_fix 데이터는 기존 방식대로 배열에 추가
        monthData.dataSource = monthData.dataSource || 'weekly_fix';
        if (item.salePrice !== null) monthData.salePrices.push(item.salePrice);
        if (item.leasePrice !== null) monthData.leasePrices.push(item.leasePrice);
      }

      if (item.saleCount !== null) monthData.saleCounts.push(item.saleCount);
      if (item.leaseCount !== null) monthData.leaseCounts.push(item.leaseCount);
    });

    // 월별로 정렬
    const months = Array.from(byMonth.keys()).sort();

    const upper = [];
    const avg = [];
    const lower = [];
    const tableRows = [];

    months.forEach((month) => {
      const data = byMonth.get(month);

      // dataSource에 따라 다르게 처리
      if (data.dataSource === 'real_transaction') {
        // 실거래 데이터: API에서 받은 max/min 사용
        const maxPrice = mode === 'sale' ? data.saleMaxPrice : data.leaseMaxPrice;
        const minPrice = mode === 'sale' ? data.saleMinPrice : data.leaseMinPrice;
        const prices = mode === 'sale' ? data.salePrices : data.leasePrices;
        const avgPrice = prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : null;

        upper.push(maxPrice);
        lower.push(minPrice);
        avg.push(avgPrice);

        tableRows.push({
          month,
          upperSale: data.saleMaxPrice,
          lowerSale: data.saleMinPrice,
          avgSale: meanOf(data.salePrices),
          upperLease: data.leaseMaxPrice,
          lowerLease: data.leaseMinPrice,
          avgLease: meanOf(data.leasePrices),
        });
      } else {
        // weekly_fix 데이터: 기존 방식대로 계산
        const prices = mode === 'sale' ? data.salePrices : data.leasePrices;

        if (prices.length > 0) {
          const maxPrice = Math.max(...prices);
          const minPrice = Math.min(...prices);
          const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
          upper.push(maxPrice);
          lower.push(minPrice);
          avg.push(avgPrice);

          tableRows.push({
            month,
            upperSale: data.salePrices.length > 0 ? Math.max(...data.salePrices) : null,
            lowerSale: data.salePrices.length > 0 ? Math.min(...data.salePrices) : null,
            avgSale: meanOf(data.salePrices),
            upperLease: data.leasePrices.length > 0 ? Math.max(...data.leasePrices) : null,
            lowerLease: data.leasePrices.length > 0 ? Math.min(...data.leasePrices) : null,
            avgLease: meanOf(data.leasePrices),
          });
        } else {
          upper.push(null);
          lower.push(null);
          avg.push(null);
          tableRows.push({
            month,
            upperSale: null,
            lowerSale: null,
            avgSale: null,
            upperLease: null,
            lowerLease: null,
            avgLease: null,
          });
        }
      }
    });

    const allPrices = [...upper, ...lower].filter((v) => Number.isFinite(v));
    const minVal = allPrices.length ? Math.min(...allPrices) : null;
    const maxVal = allPrices.length ? Math.max(...allPrices) : null;
    const range = minVal != null && maxVal != null ? `${formatEok1(minVal * 10000)} ~ ${formatEok1(maxVal * 10000)}` : '-';

    let minPoint = null;
    if (mode === 'sale') {
      lower.forEach((v, idx) => {
        if (!Number.isFinite(v)) return;
        if (!minPoint || v < minPoint.value[1]) minPoint = { value: [idx, v] };
      });
    }

    return {
      labels: months,
      upperSeries: upper,
      avgSeries: avg,
      lowerSeries: lower,
      rangeText: range,
      minLowerPoint: minPoint,
      tableData: tableRows,
    };
  }, [marketItems, mode, viewMode]);

  // 매물량(매매/전세 건수)을 호가 차트 라벨에 정렬 — 실거래 차트처럼 회색 막대로 오버레이
  // 통합 API(marketItems)의 saleCount/leaseCount 를 그대로 사용 (호가 라인과 동일 소스/날짜)
  const volumeSeries = useMemo(() => {
    const emptyForLabels = labels.map(() => null);
    const raw = Array.isArray(marketItems) ? marketItems : [];
    if (!raw.length || !labels.length) return emptyForLabels;

    const points = raw
      .map((r) => {
        const date = toDateKey(r?.date);
        const sale = Number(r?.saleCount);
        const lease = Number(r?.leaseCount);
        return {
          date,
          value: mode === 'sale'
            ? (Number.isFinite(sale) ? sale : null)
            : (Number.isFinite(lease) ? lease : null),
        };
      })
      .filter((p) => p.date && p.value !== null)
      .sort((a, b) => a.date.localeCompare(b.date));

    if (!points.length) return emptyForLabels;

    if (viewMode === 'weekly') {
      // 각 주(날짜) 라벨에 가장 가까운 스냅샷을 매핑 (2주 이상 벗어나면 표시 안 함)
      const times = points.map((p) => ({ t: new Date(p.date).getTime(), value: p.value }));
      return labels.map((label) => {
        const target = new Date(toDateKey(label)).getTime();
        if (!Number.isFinite(target)) return null;
        let best = null;
        let bestDiff = Infinity;
        times.forEach((p) => {
          const diff = Math.abs(p.t - target);
          if (diff < bestDiff) { bestDiff = diff; best = p.value; }
        });
        return bestDiff <= 14 * 24 * 3600 * 1000 ? best : null;
      });
    }

    // 월별: 해당 월의 마지막(최신) 스냅샷 사용 — 매물량은 재고(stock) 성격
    const byMonth = new Map();
    points.forEach((p) => { byMonth.set(p.date.slice(0, 7), p.value); });
    return labels.map((label) => {
      const m = String(label).slice(0, 7);
      return byMonth.has(m) ? byMonth.get(m) : null;
    });
  }, [marketItems, labels, mode, viewMode]);

  const hasVolumeData = useMemo(
    () => volumeSeries.some((v) => Number.isFinite(v) && v > 0),
    [volumeSeries]
  );

  const option = useMemo(() => {
    console.log('Chart option recalculating - mode:', mode, 'targetPrice:', targetPrice, 'labels:', labels.length);

    const yVals = [...upperSeries, ...avgSeries, ...lowerSeries].filter((v) => Number.isFinite(v));
    // 타겟가도 Y축 범위 계산에 포함
    if (mode === 'sale' && targetPrice != null && Number.isFinite(targetPrice)) {
      yVals.push(targetPrice);
    }
    const min = yVals.length ? Math.min(...yVals) : 0;
    const max = yVals.length ? Math.max(...yVals) : 100000000;
    const pad = (max - min) * 0.1;

    console.log('Y-axis range:', { min, max, pad, targetPrice });

    // Y축 포맷 함수 (만원 단위 입력값)
    const formatYAxis = (v) => {
      const num = Number(v);
      if (!Number.isFinite(num)) return '';
      if (num === 0) return '0';
      // v는 이미 만원 단위 → 원 단위로 변환 후 억 소수1자리(24.3억)로 통일
      return formatEok1(num * 10000);
    };

    const volValues = volumeSeries.filter((v) => Number.isFinite(v));
    const hasVolume = volValues.some((v) => v > 0);
    const maxVolume = volValues.length ? Math.max(...volValues) : 0;
    const volumeName = mode === 'sale' ? '매매 매물량' : '전세 매물량';

    const series = [
      {
        name: '상위 호가',
        type: 'line',
        data: upperSeries,
        smooth: false,
        symbol: 'circle',
        symbolSize: 3,
        lineStyle: { color: '#ef4444', width: 2.5, type: [5, 5] },
        itemStyle: { color: '#ef4444' },
      },
      {
        name: '최적 호가',
        type: 'line',
        data: avgSeries,
        smooth: false,
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: { color: '#10b981', width: 3 },
        itemStyle: { color: '#10b981' },
      },
      {
        name: '하위 호가',
        type: 'line',
        data: lowerSeries,
        smooth: false,
        symbol: 'circle',
        symbolSize: 3,
        lineStyle: { color: '#3b82f6', width: 2.5, type: [5, 5] },
        itemStyle: { color: '#3b82f6' },
      },
    ];

    // 매물량 회색 막대 (우측 y축, 건 단위) — 호가 선 뒤에 깔림
    if (hasVolume) {
      series.unshift({
        name: volumeName,
        type: 'bar',
        yAxisIndex: 1,
        data: volumeSeries,
        barWidth: 10,
        itemStyle: { color: 'rgba(148,163,184,0.5)', borderRadius: [2, 2, 0, 0] },
        z: 1,
      });
    }

    // 타겟가 별표 추가 (매매 모드일 때만)
    if (mode === 'sale' && targetPrice != null && Number.isFinite(targetPrice) && labels.length > 0) {
      // 차트 중앙보다 약간 왼쪽에 별표 표시 (40% 지점)
      const upperMiddleIndex = Math.floor(labels.length * 0.4);
      console.log('Adding target price marker:', { targetPrice, upperMiddleIndex, labels: labels.length, middleLabel: labels[upperMiddleIndex] });

      series.push({
        name: '타겟가',
        type: 'scatter',
        data: [[upperMiddleIndex, targetPrice]],
        symbol: STAR_SYMBOL_PATH,
        symbolSize: 35,
        itemStyle: {
          color: '#fbbf24',
          borderColor: '#f59e0b',
          borderWidth: 3,
        },
        emphasis: {
          scale: 1.3,
          itemStyle: {
            color: '#fbbf24',
            borderColor: '#f59e0b',
            borderWidth: 3,
          }
        },
        label: {
          show: true,
          position: 'top',
          offset: [0, -15],
          formatter: () => `타겟가: ${formatEok1(targetPrice * 10000)}`,
          color: '#dc2626',
          fontSize: 12,
          fontWeight: 700,
          backgroundColor: '#fffbeb',
          padding: [6, 12],
          borderRadius: 4,
          borderColor: '#f59e0b',
          borderWidth: 2,
        },
        z: 100,
      });
    } else {
      console.log('Target price NOT added - reasons:', {
        mode,
        modeIsSale: mode === 'sale',
        targetPrice,
        targetPriceIsNull: targetPrice == null,
        isFinite: Number.isFinite(targetPrice),
        labelsLength: labels.length,
        hasLabels: labels.length > 0
      });
    }

    return {
      grid: { left: 65, right: hasVolume ? 46 : 18, top: 60, bottom: 35 },
      dataZoom: [{
        type: 'inside',
        start: 0,
        end: 100,
        zoomOnMouseWheel: false,
        moveOnMouseMove: true,
        moveOnMouseWheel: true,
      }],
      tooltip: {
        trigger: 'axis',
        formatter: (params = []) => {
          const map = new Map(params.map((p) => [p?.seriesName, p]));
          const upper = map.get('상위 호가');
          const avg = map.get('최적 호가');
          const lower = map.get('하위 호가');
          const axis = params[0]?.axisValue || '-';
          // 만원 단위를 원 단위로 변환하여 formatEok1 호출
          const line = (name, color, val) => {
            const priceInWon = val * 10000;
            return `<div style="display:flex;justify-content:space-between;gap:14px;margin-top:2px;">
              <span><span style="display:inline-block;width:8px;height:8px;border-radius:999px;background:${color};margin-right:6px;"></span>${name}</span>
              <strong>${formatEok1(priceInWon)}</strong>
            </div>`;
          };
          const lines = [
            `<div class="tx-tooltip-title">${formatDotDate(axis)}</div>`,
          ];
          if (upper?.data != null) lines.push(line('상위호가', '#ef4444', upper.data));
          if (avg?.data != null) lines.push(line('최적호가', '#10b981', avg.data));
          if (lower?.data != null) lines.push(line('하위호가', '#3b82f6', lower.data));
          // 타겟가는 scatter 타입이므로 data가 배열 형태 [x, y]
          if (mode === 'sale' && targetPrice != null && Number.isFinite(targetPrice)) {
            lines.push(line('타겟가', '#fbbf24', targetPrice));
          }
          // 매물량(건수)은 가격이 아니므로 별도 포맷
          const vol = map.get(volumeName);
          if (vol && vol.data != null && Number.isFinite(Number(vol.data))) {
            lines.push(`<div style="display:flex;justify-content:space-between;gap:14px;margin-top:2px;">
              <span><span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:#94a3b8;margin-right:6px;"></span>매물량</span>
              <strong>${Number(vol.data).toLocaleString('ko-KR')}건</strong>
            </div>`);
          }
          return lines.filter(Boolean).join('');
        },
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisLabel: {
          formatter: (v) => formatDotDate(v),
          fontSize: 10,
          rotate: 0,
        }
      },
      yAxis: [
        {
          type: 'value',
          min: Math.max(0, min - pad),
          max: max + pad,
          axisLabel: {
            formatter: formatYAxis,
            fontSize: 10,
          },
          splitNumber: 5,
        },
        ...(hasVolume ? [{
          type: 'value',
          position: 'right',
          min: 0,
          // 막대를 하단 영역으로 눌러 호가 선과 겹치지 않게 함
          max: Math.max(1, Math.ceil(maxVolume * 3.2)),
          axisLabel: {
            formatter: (v) => `${Math.round(v)}건`,
            fontSize: 10,
            color: '#94a3b8',
          },
          splitLine: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
        }] : []),
      ],
      legend: {
        show: false,
      },
      series,
    };
  }, [labels, upperSeries, avgSeries, lowerSeries, minLowerPoint, mode, targetPrice, viewMode, volumeSeries]);

  const hasData = labels.length > 0 && (upperSeries.some(v => Number.isFinite(v)) || avgSeries.some(v => Number.isFinite(v)) || lowerSeries.some(v => Number.isFinite(v)));

  const handleChartWheel = (event) => {
    event.preventDefault();
    if (event.deltaY !== 0) {
      setViewMode(prev => prev === 'monthly' ? 'weekly' : 'monthly');
    }
  };

  return (
    <section className="tx-sub-chart-section">
      <div className="tx-sub-chart-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
          <h4>매물 호가</h4>
          <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: '#5b6474' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444' }}></span>
              상위 호가
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }}></span>
              최적 호가
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6' }}></span>
              하위 호가
            </span>
            {hasVolumeData && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '2px', background: '#94a3b8' }}></span>
                {mode === 'sale' ? '매매 매물량' : '전세 매물량'}(건)
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div className="tx-sub-toggle">
            <button type="button" className={mode === 'sale' ? 'active' : ''} onClick={() => handleModeChange('sale')}>매매</button>
            <button type="button" className={mode === 'lease' ? 'active' : ''} onClick={() => handleModeChange('lease')}>전세</button>
          </div>
        </div>
      </div>
      {loading ? (
        <div className="ranking-detail-modal-status" style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          호가 데이터를 불러오는 중입니다...
        </div>
      ) : hasData ? (
        <div className="tx-sub-content-row">
          <div className="tx-sub-chart-pane" style={{ width: '100%', minWidth: 0 }} onWheel={handleChartWheel}>
            <ReactECharts option={option} style={{ width: '100%', height: 340 }} notMerge lazyUpdate />
          </div>
          <div className="tx-sub-table-pane" style={{ width: '100%', minWidth: 0, maxHeight: '360px', overflowY: 'auto' }}>
            <table className="tx-hybrid-table-v2">
              <thead>
                <tr>
                  <th>날짜</th>
                  <th>상위호가</th>
                  <th>최적호가</th>
                  <th>하위호가</th>
                </tr>
              </thead>
              <tbody>
                {[...tableData].reverse().map((row, idx) => {
                  const dateKey = row.month || row.date || idx;
                  const displayDate = formatDotDate(row.month || row.date);

                  return (
                    <tr key={dateKey}>
                      <td>{displayDate}</td>
                      {mode === 'sale' ? (
                        <>
                          <td className="price">{row.upperSale != null ? formatEok1(row.upperSale * 10000) : '-'}</td>
                          <td className="price">{row.avgSale != null ? formatEok1(row.avgSale * 10000) : '-'}</td>
                          <td className="price">{row.lowerSale != null ? formatEok1(row.lowerSale * 10000) : '-'}</td>
                        </>
                      ) : (
                        <>
                          <td className="price">{row.upperLease != null ? formatEok1(row.upperLease * 10000) : '-'}</td>
                          <td className="price">{row.avgLease != null ? formatEok1(row.avgLease * 10000) : '-'}</td>
                          <td className="price">{row.lowerLease != null ? formatEok1(row.lowerLease * 10000) : '-'}</td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="ranking-detail-modal-status">호가 데이터가 없습니다.</div>
      )}
    </section>
  );
}

export default AskPriceChartSection;
